import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import CSPortfolioComponent from '../cs/CSPortfolioComponent';
import { homeProjects, homeSections } from '../cs/csProjects';

/**
 * The CS work, sat on the home page between the landing and the contact screen, so it is
 * on the page someone arrives at rather than one click away. The same component the
 * /cs-portfolio page renders — that page is kept as it was, and this is a second view of
 * it, not a move.
 *
 * Two things are different here: a selection of the run rather than all of it (see
 * homeProjects in cs/csProjects), and the framing — there is no landing of its own, so a
 * heading says what the band is and links through to the page that has the rest.
 */
export default function HomeCSSection() {
    return (
        // snap-start, and nothing else inside: under the home page's mandatory snapping
        // a stretch with no stop in it is scrolled straight past, and this band is many
        // viewports tall. A snap area taller than the viewport snaps only at its edges,
        // which leaves the whole run free to scroll through. See `.snap-page` in index.css.
        // No data-header-fill: the run's ground is black already, which is the fill the
        // header falls back to — see page_assets/header.
        <section className="relative w-full bg-black snap-start">
            {/* The portfolio page's own backdrop, which belongs to the run rather than to
                that page: the cards read as cut-outs laid over graph paper. */}
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                    backgroundImage: `
                        linear-gradient(rgba(255, 255, 255) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255, 255, 255) 1px, transparent 1px)
                    `,
                    backgroundSize: '20px 20px',
                }}
            />
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: `
                        linear-gradient(to bottom,
                            rgba(0, 0, 0, 0.5) 0%,
                            rgba(0, 0, 0, 0.8) 30%,
                            rgba(0, 0, 0, 0.9) 60%,
                            rgba(0, 0, 0, 1) 100%
                        )
                    `,
                }}
            />

            <div className="relative z-10 w-full flex flex-col items-center pb-20">
                <div className="w-full max-w-6xl px-4 sm:px-8 pt-20">
                    <div className="flex flex-wrap items-end justify-between gap-4 border-b border-yellow-500/30 pb-4">
                        {/* Zaslia, the display face every other heading on the site is
                            set in — see page_assets/HeroBanner, where the landings' own
                            titles and headings carry it. It draws smaller than a system
                            face at the same size, hence the step up from the 2xl/3xl a
                            sans heading would want here. */}
                        <h2
                            className="text-4xl sm:text-5xl font-bold text-yellow-400 leading-none"
                            style={{ fontFamily: '"Zaslia", serif' }}
                        >
                            Computer Science Portfolio
                        </h2>
                        <Link
                            to="/cs-portfolio"
                            className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-yellow-400/80 hover:text-yellow-200 transition-colors"
                        >
                            See all projects
                            <ArrowUpRight className="w-3 h-3" />
                        </Link>
                    </div>
                </div>

                {/* The component carries its own top padding, which is the gap under the
                    heading above — so nothing is added between the two.

                    No wheel: it is worth its screen space over the full run, but six cards
                    are a short enough scroll that a jump-to-category saves nothing, and on
                    the home page it would be a second nav hanging over a page that already
                    has the header. */}
                <CSPortfolioComponent
                    projects={homeProjects}
                    sections={homeSections}
                    accent="yellow"
                    radialNav={false}
                />
            </div>
        </section>
    );
}
